import { Alert, Empty } from "antd";
import { resolveLocation } from "common/locationResolver";
import { showError, showSuccess, showWarning } from "common/ToastNotifications";
import EmptyDataRow from "components/atoms/EmptyDataRow";
import LoadingOverlay from "components/atoms/LoadingOverlay";
import MenuPanelContainer from "components/atoms/MenuPanelContainer";
import NotFoundError from "components/atoms/NotFoundError";
import SidePanel from "components/atoms/SidePanel";
import SuccessResult from "components/atoms/SuccessResult";
import AttachmentTab from "components/molecules/AttachmentTab";
import BasicFormBuilder from "components/molecules/BasicFormBuilder";
import DeduplicatedRecordsTable from "components/molecules/DeduplicatedRecordsTable";
import OkCancelConfirmationBox from "components/molecules/OkCancelConfirmationBox";
import ProhibitedArea from "components/molecules/ProhibitedArea";
import DataSetExport from "components/organisms/DataSetExport";
import DataSetList from "components/organisms/DataSetList";
import ReviewQueueButton from "components/molecules/ReviewQueueButton";
import ManageDataSet from "components/organisms/ManageDataSet";
import PipelineBuilder from "components/organisms/PipelineBuilder/PipelineBuilder";
import AppPaths from "constants/appPaths";
import { mergedRecordLinks } from "common/mergedRecord";
import MergedRecordNotice from "components/molecules/MergedRecordNotice";
import flatten from "flat";
import _ from "lodash";
import PropTypes from "prop-types";
import { Component } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import actions from "store/actions";
import Annotations from "./Annotations";
import Comments from "./Comments";
import DataObjectConsents from "./DataObjectConsents";
import DataObjectVerifications from "./DataObjectVerifications";
import DataSetAudit from "./DataSetAudit";
import LocationContainer from "./LocationMain";

export class DataSets extends Component {
    constructor(props) {
        super(props);
        this.state = {
            fetchingSchemaDataSets: false,
            fetchingDataSetPermission: false,
            fetchingDataSet: false,
            fetchingCommentsPermission: false,
            busy: false,
            busyMessage: "Please wait...",
            manageDataSetErrors: {},
            showExportJobCreatedResult: false,
            showExportJobToSystemConnectionCreatedResult: false,
            sidePanelActiveTab: "DATASET_RECORD",
            openDatasetCommentsFromUrl: false,
            desiredDataObjectIdFromUrl: null,
            desiredDataObjectTabFromUrl: null,
            annotationFiltersFromUrl: null,
            annotationFiltersRecordId: null,
            annotationFiltersApplied: false,
            pipelineBuilderVisible: false,
            initialPipelineId: null,
        };
    }

    componentDidMount() {
        this.props.clearSearchDataSetDataObjects();
        this.props.getDataObjectPermission();
        this.props.getCommentsPermission();
        this.setState({
            fetchingCommentsPermission: true,
        });

        const params = new URLSearchParams(this.props.location ? this.props.location.search : window.location.search);
        if (params.get("dataset_comment") === "true") {
            this.setState({ openDatasetCommentsFromUrl: true });
        }
        const dataObjectId = params.get("dataobject") || params.get("dataObjectId") || params.get("dataobject_id");
        if (dataObjectId) {
            if (params.get("dataobject_annotation") === "true") {
                const closedParam = params.get("closed");
                const shouldFilterClosed = closedParam && closedParam.toLowerCase() === "true";
                this.setState({
                    desiredDataObjectIdFromUrl: dataObjectId,
                    desiredDataObjectTabFromUrl: "ANNOTATIONS",
                    ...(shouldFilterClosed
                        ? {
                              annotationFiltersFromUrl: { closed: true },
                              annotationFiltersRecordId: dataObjectId,
                              annotationFiltersApplied: false,
                          }
                        : {}),
                });
            } else if (params.get("dataobject_comment") === "true") {
                this.setState({ desiredDataObjectIdFromUrl: dataObjectId, desiredDataObjectTabFromUrl: "COMMENTS" });
            }
        }

        const pipelineId = params.get("pipelineId");
        if (pipelineId) {
            // Store the pipeline ID - open builder immediately if dataset already loaded, otherwise wait
            if (this.props.dataSet?.dataSetId) {
                this.setState({ pipelineBuilderVisible: true, initialPipelineId: pipelineId });
            } else {
                this.setState({ initialPipelineId: pipelineId });
            }
        }

        if (this.props.action) {
            this.showBreadCrumbNavigationItems();
            if (this.props.action === "history") {
                this.setState({
                    showHistory: false,
                });
            }
            this.setState({
                fetchingDataSetPermission: true,
            });
            this.props.getUserDataSetPermission();

            if (this.props.dataSetId) {
                this.setState({
                    fetchingDataSet: true,
                });
                this.props.getDataSetById(this.props.schemaModel.businessAreaId, this.props.schemaModel.schemaId, this.props.dataSetId);
            }
        } else {
            if (this.props.selectedSchemaModel) {
                this.setState({
                    fetchingSchemaDataSets: true,
                    fetchingDataSetPermission: true,
                });
                this.props.getDataSetBySchemaId(this.props.selectedSchemaModel.businessAreaId, this.props.selectedSchemaModel.schemaId);
                this.props.getUserDataSetPermission();
                this.props.getUserExportJobPermission();
            }
        }
    }

    componentWillUnmount() {
        this.props.clearSearchDataSetDataObjects();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        this.onGetUserDataSetPermissionResultChanged(prevProps);
        this.onGetSchemaDataSetResultChanged(prevProps);
        this.onCreateDataSetResultChanged(prevProps);
        this.onUpdateDataSetResultChanged(prevProps);
        this.onDeleteDataSetResultChanged(prevProps);
        this.onGetDataSetByIdResultChanged(prevProps);
        this.onGetDataObjectsByDataSetIdResultChanged(prevProps);
        this.onGetDataObjectsByDataSetIdNextPageResultChanged(prevProps);
        this.onGetJobsResultChanged(prevProps);
        this.onUndoJobResultChanged(prevProps);
        this.onIndexDataSetResultChanged(prevProps);
        this.onExportDataSetToFileResultChanged(prevProps);
        this.onExportDataSetToSystemConnectionResultChanged(prevProps);
        this.onCreateDataObjectResultChanged(prevProps);
        this.onUpdateDataObjectResultChanged(prevProps);
        this.onDeleteDataObjectResultChanged(prevProps);
        this.onGetDataObjectResultChanged(prevProps);
        this.onPushedDataObjectChanged(prevProps);
        this.onGetDeDuplicatedRecordsRequestResultChanged(prevProps);
        this.onGetDataObjectPermissionResultChanged(prevProps);
        this.onSearchDataObjectsResultChanged(prevProps);
        this.onGetCommentsPermissionResultChanged(prevProps);

        // Handle pipelineId query param changes (e.g., navigation from view detail page)
        if (this.props.location?.search !== prevProps.location?.search) {
            const params = new URLSearchParams(this.props.location?.search || "");
            const pipelineId = params.get("pipelineId");
            if (pipelineId && pipelineId !== this.state.initialPipelineId) {
                // Open builder immediately if dataset already loaded, otherwise wait
                if (this.props.dataSet?.dataSetId) {
                    this.setState({ pipelineBuilderVisible: true, initialPipelineId: pipelineId });
                } else {
                    this.setState({ initialPipelineId: pipelineId });
                }
            }
        }
        if (
            this.state.annotationFiltersFromUrl &&
            !this.state.annotationFiltersApplied &&
            this.state.selectedDataObject &&
            this.state.selectedDataObject.dataObjectId === this.state.annotationFiltersRecordId
        ) {
            this.setState({ annotationFiltersApplied: true });
        }
        this.checkAndOpenRecordTabsFromUrl(prevProps, prevState);
    }

    checkAndOpenRecordTabsFromUrl = (prevProps, prevState) => {
        if (this.state.desiredDataObjectIdFromUrl && this.props.dataObjectsByDataSetIdResult !== prevProps.dataObjectsByDataSetIdResult) {
            const desired = this.state.desiredDataObjectIdFromUrl;
            const items = this.props.dataObjectsByDataSetIdResult?.Items;
            const found = items?.find((d) => d.dataObjectId === desired);
            if (found) {
                this.openRecordFromUrl(found);
            } else if (items && this.state.urlDataObjectIdRequested !== desired && this.props.schemaModel && this.props.dataSet) {
                // Not among the records loaded: read it by its id (it may be further on, or merged).
                this.setState({ urlDataObjectIdRequested: desired });
                this.props.getDataObject(this.props.schemaModel.businessAreaId, this.props.schemaModel.schemaId, this.props.dataSet.dataSetId, desired);
            }
        }
        if (this.state.openDatasetCommentsFromUrl && this.props.getCommentsPermissionResult && !this.state.fetchingCommentsPermission) {
            this.setState({ openDatasetCommentsFromUrl: false });
        }
    };

    openRecordFromUrl = (dataObject) => {
        this.setState({
            selectedDataObject: dataObject,
            showDataObjectFormContainer: true,
            sidePanelActiveTab: this.state.desiredDataObjectTabFromUrl || "COMMENTS",
            desiredDataObjectIdFromUrl: null,
            desiredDataObjectTabFromUrl: null,
            urlDataObjectIdRequested: null,
        });
    };

    // The record a link asked for, read by its id: opened, or (PRE-2837) a notice saying which record
    // deduplication merged it into.
    onUrlDataObjectRead = () => {
        const requested = this.state.urlDataObjectIdRequested;
        const result = this.props.getDataObjectResult;
        const answeredId = result.success ? this.props.dataObject && this.props.dataObject.dataObjectId : result.dataObjectId;
        // Another record's (an update pushed for a row on screen): still waiting for the link's.
        if (answeredId !== requested) {
            return;
        }
        if (result.success) {
            this.openRecordFromUrl(this.props.dataObject);
            return;
        }
        this.setState({ desiredDataObjectIdFromUrl: null, desiredDataObjectTabFromUrl: null, urlDataObjectIdRequested: null });
        if (result.survivor) {
            this.setState({ mergedRecordNotice: { message: result.message, survivor: result.survivor } });
        } else {
            showError("The record in this link could not be found.");
        }
    };

    // Reads a record this screen opens (a row clicked, a record saved). Only one read runs at a time,
    // so one for another record than a link's cancels the link's: the link is let go, rather than
    // its record opening later out of the blue.
    readDataObject = (dataObjectId) => {
        if (this.state.urlDataObjectIdRequested && this.state.urlDataObjectIdRequested !== dataObjectId) {
            this.setState({ desiredDataObjectIdFromUrl: null, desiredDataObjectTabFromUrl: null, urlDataObjectIdRequested: null });
        }
        this.props.getDataObject(this.props.schemaModel.businessAreaId, this.props.schemaModel.schemaId, this.props.dataSet.dataSetId, dataObjectId);
    };

    // Opens the record a merged one went into (from the notice), as a link to it would.
    openSurvivorRecord = () => {
        const { survivorId } = this.mergedRecordNoticeLinks();
        this.setState({ mergedRecordNotice: null, desiredDataObjectIdFromUrl: survivorId, desiredDataObjectTabFromUrl: "COMMENTS", urlDataObjectIdRequested: survivorId });
        this.props.getDataObject(this.props.schemaModel.businessAreaId, this.props.schemaModel.schemaId, this.props.dataSet.dataSetId, survivorId);
    };

    mergedRecordNoticeLinks = () =>
        mergedRecordLinks({
            tenant: this.props.tenant,
            businessArea: this.props.businessArea,
            schema: this.props.schema,
            schemaId: this.props.schemaModel && this.props.schemaModel.schemaId,
            survivor: this.state.mergedRecordNotice && this.state.mergedRecordNotice.survivor,
        });

    onSearchDataObjectsResultChanged = (prevProps) => {
        if (this.props.searchDataObjectsResult && this.props.searchDataObjectsResult !== prevProps.searchDataObjectsResult) {
            if (!this.props.searchDataObjectsResult.success) {
                showError("Unable to search data objects.");
            }
        }
    };

    onGetCommentsPermissionResultChanged = (prevProps) => {
        if (this.props.getCommentsPermissionResult && this.props.getCommentsPermissionResult !== prevProps.getCommentsPermissionResult) {
            if (!this.props.getCommentsPermissionResult.success) {
                showError("Could not retrieve comments permissions.");
            }
            this.setState({
                fetchingCommentsPermission: false,
            });
        }
    };

    onGetDataObjectPermissionResultChanged = (prevProps) => {
        if (this.props.getDataObjectPermissionResult && this.props.getDataObjectPermissionResult !== prevProps.getDataObjectPermissionResult) {
            if (!this.props.getDataObjectPermissionResult.success || !this.props.dataObjectPermission || !this.props.dataObjectPermission?.canEdit) {
                this.setState({
                    permissionToEditDataObjectDenied: true,
                });
            }
            this.setState({
                fetchingPermission: false,
            });
        }
    };

    showBreadCrumbNavigationItems = () => {
        if (this.props.setBreadcrumbNavigationItems) {
            let breadCrumbItems = [
                {
                    route: AppPaths.TENANT_HOME.replace(":tenant", this.props.tenant.toLowerCase()),
                    text: "Home",
                },
                {
                    route: AppPaths.TENANT_BUSINESS_AREA_SCHEMA.replace(":tenant", this.props.tenant.toLowerCase()).replace(":businessarea", this.props.businessArea.toLowerCase()),
                    text: "Schemas",
                },
                {
                    route: AppPaths.TENANT_BUSINESS_AREA_SCHEMA_ID.replace(":tenant", this.props.tenant.toLowerCase())
                        .replace(":businessarea", this.props.businessArea.toLowerCase())
                        .replace(":schemaId", this.props.schemaModel.schemaId),
                    text: this.props.schema,
                },
                {
                    route: `${AppPaths.TENANT_BUSINESS_AREA_SCHEMA.replace(":tenant", this.props.tenant.toLowerCase()).replace(
                        ":businessarea",
                        this.props.businessArea.toLowerCase(),
                    )}${this.props.schemaModel ? `#${this.props.schemaModel.name}` : ""}`,
                    text: "Datasets",
                },
            ];
            if (this.props.action) {
                let dataSetName = "";
                switch (this.props.action.toLowerCase()) {
                    case "create":
                        breadCrumbItems.push({
                            route: "",
                            text: "New Dataset",
                        });
                        break;
                    case "view":
                    case "edit":
                        breadCrumbItems.push(
                            ...[
                                {
                                    route: "",
                                    text: this.getDataSetName(),
                                },
                            ],
                        );
                        break;
                    case "history":
                        dataSetName = this.getDataSetName();
                        breadCrumbItems.push(
                            ...[
                                {
                                    route: "",
                                    text: dataSetName,
                                },
                                {
                                    route: "",
                                    text: "History",
                                },
                            ],
                        );
                        break;
                    case "export":
                        dataSetName = this.getDataSetName();
                        breadCrumbItems.push(
                            ...[
                                {
                                    route: "",
                                    text: dataSetName,
                                },
                                {
                                    route: "",
                                    text: "Export",
                                },
                            ],
                        );
                        break;
                    default:
                        // Always include a default break
                        break;
                }
            }

            this.props.setBreadcrumbNavigationItems(breadCrumbItems);
        }
    };

    getDataSetName = () => {
        let dataSetName = "";
        if (this.props.dataSet && this.props.dataSet.dataSetId === this.props.dataSetId) {
            dataSetName = this.props.dataSet.dataSetName;
        }

        if (!dataSetName && this.props.schemaDataSetResult && this.props.schemaDataSetResult.Items && this.props.schemaDataSetResult.Items.length > 0) {
            let dataSet = this.props.schemaDataSetResult.Items.find((dataSet) => dataSet.dataSetId === this.props.dataSetId);
            if (dataSet) {
                dataSetName = dataSet.dataSetName;
            }
        }
        if (!dataSetName && this.props.recentDataSetListResult && this.props.recentDataSetListResult.Items && this.props.recentDataSetListResult.Items.length > 0) {
            let dataSet = this.props.recentDataSetListResult.Items.find((dataSet) => dataSet.dataSetId === this.props.dataSetId);
            if (dataSet) {
                dataSetName = dataSet.dataSetName;
            }
        }
        if (!dataSetName) {
            dataSetName = this.props.dataSetId;
        }
        return dataSetName;
    };

    onGetUserDataSetPermissionResultChanged = (prevProps) => {
        if (this.props.getUserDataSetPermissionResult && this.props.getUserDataSetPermissionResult !== prevProps.getUserDataSetPermissionResult) {
            if (!this.props.getUserDataSetPermissionResult.success || !this.props.dataSetPermission) {
                this.setState({
                    permissionDenied: true,
                });
            } else {
                this.setDataSetPermissions(this.props.dataSetPermission, this.props.userBusinessArea);
            }
            this.setState({
                fetchingDataSetPermission: false,
            });
        }
    };

    onGetSchemaDataSetResultChanged = (prevProps) => {
        if (this.props.getSchemaDataSetResult && this.props.getSchemaDataSetResult !== prevProps.getSchemaDataSetResult) {
            if (!this.props.getSchemaDataSetResult.success) {
                if (this.props.getSchemaDataSetResult.code === "PERMISSION_DENIED") {
                    this.setState({
                        permissionDenied: true,
                    });
                } else {
                    showError("Unable to get schema datasets.");
                }
            }
            this.setState({
                fetchingSchemaDataSets: false,
            });
        }
    };

    onCreateDataSetResultChanged = (prevProps) => {
        if (this.props.createDataSetResult && this.props.createDataSetResult !== prevProps.createDataSetResult) {
            if (!this.props.createDataSetResult.success) {
                if (this.props.createDataSetResult.code === "PERMISSION_DENIED") {
                    this.setState({
                        busy: false,
                    });
                    showError("Unable to create dataset. Permission Denied.");
                } else {
                    this.setState({
                        busy: false,
                        manageDataSetErrors: this.props.createDataSetResult.fields,
                    });
                    showError("Unable to create dataset.");
                }
            } else {
                this.setState({
                    manageDataSetErrors: null,
                });
                if (this.state.afterSaveCallback) {
                    this.state.afterSaveCallback();
                } else {
                    this.setState({
                        busy: true,
                        busyMessage: "Dataset created. Please wait...",
                    });
                    showSuccess("Dataset created successfully.", () => {
                        this.goToSchemas();
                    });
                }
            }
            this.setState({
                afterSaveCallback: null,
            });
        }
    };

    onUpdateDataSetResultChanged = (prevProps) => {
        if (this.props.updateDataSetResult && this.props.updateDataSetResult !== prevProps.updateDataSetResult) {
            if (!this.props.updateDataSetResult.success) {
                if (this.props.updateDataSetResult.code === "PERMISSION_DENIED") {
                    this.setState({
                        busy: false,
                    });
                    showError("Unable to update dataset. Permission Denied.");
                } else {
                    this.setState({
                        busy: false,
                        manageDataSetErrors: this.props.updateDataSetResult.fields,
                    });
                    showError("Unable to update dataset.");
                }
            } else {
                this.setState({
                    manageDataSetErrors: null,
                });
                if (this.state.afterSaveCallback) {
                    this.state.afterSaveCallback();
                } else if (this.props.updateDataSetResult.source === "assistant") {
                    this.setState({ busy: false });
                } else {
                    this.setState({
                        busy: true,
                        busyMessage: "Dataset updated. Please wait...",
                    });
                    showSuccess("Dataset updated successfully.", () => {
                        this.goToSchemas();
                    });
                }
            }
            this.setState({
                afterSaveCallback: null,
            });
        }
    };

    onDeleteDataSetResultChanged = (prevProps) => {
        if (this.props.deleteDataSetResult && this.props.deleteDataSetResult !== prevProps.deleteDataSetResult) {
            if (!this.props.deleteDataSetResult.success) {
                if (this.props.deleteDataSetResult.code === "PERMISSION_DENIED") {
                    showError("Permission denied!");
                } else {
                    showError("Unable to delete dataset.");
                }
            } else {
                showSuccess("Dataset deleted successfully.");
            }
            this.setState({
                busy: false,
                busyMessage: "",
            });
        }
    };

    onGetDataSetByIdResultChanged = (prevProps) => {
        if (this.props.getDataSetByIdResult && this.props.getDataSetByIdResult !== prevProps.getDataSetByIdResult) {
            if (!this.props.getDataSetByIdResult.success) {
                if (this.props.getDataSetByIdResult.code === "PERMISSION_DENIED") {
                    this.setState({
                        permissionDenied: true,
                    });
                } else {
                    showError("Unable to retrieve Dataset.");
                }
            } else {
                if (!this.props.schemaDataSetResult || !this.props.schemaDataSetResult.Items || this.props.schemaDataSetResult.Items.length === 0) {
                    this.showBreadCrumbNavigationItems();
                }
                if (this.props.action === "history") {
                    this.setState({
                        showHistory: true,
                    });
                } else {
                    if (this.props.dataSet) {
                        this.getDataSetObjects();
                    }
                }
                // Open pipeline builder if we have a pending pipelineId from URL
                if (this.state.initialPipelineId && !this.state.pipelineBuilderVisible) {
                    this.setState({ pipelineBuilderVisible: true });
                }
            }
            this.setState({
                fetchingDataSet: false,
            });
        }
    };

    onGetDataObjectsByDataSetIdResultChanged = (prevProps) => {
        if (this.props.getDataObjectsByDataSetIdResult && this.props.getDataObjectsByDataSetIdResult !== prevProps.getDataObjectsByDataSetIdResult) {
            if (!this.props.getDataObjectsByDataSetIdResult.success) {
                if (this.props.getDataObjectsByDataSetIdResult.code === "PERMISSION_DENIED") {
                    this.setState({
                        dataObjectsPermissionDenied: true,
                    });
                } else {
                    showError("Unable to get data objects.");
                }
            }
            this.setState({
                fetchingDataObjects: false,
            });
        }
    };

    onGetDataObjectsByDataSetIdNextPageResultChanged = (prevProps) => {
        if (this.props.getDataObjectsByDataSetIdNextPageResult && this.props.getDataObjectsByDataSetIdNextPageResult !== prevProps.getDataObjectsByDataSetIdNextPageResult) {
            if (!this.props.getDataObjectsByDataSetIdNextPageResult.success) {
                if (this.props.getDataObjectsByDataSetIdNextPageResult.code === "PERMISSION_DENIED") {
                    this.setState({
                        dataObjectsPermissionDenied: true,
                    });
                } else {
                    showError("Unable to get data objects.");
                }
            }
            this.setState({
                fetchingNextPageDataObjects: false,
            });
        }
    };

    onGetJobsResultChanged = (prevProps) => {
        if (this.props.getJobsResult && this.props.getJobsResult !== prevProps.getJobsResult) {
            if (!this.props.getJobsResult.success) {
                showError("Unable to get batch upload jobs.");
            }
            this.setState({
                fetchingDataSetJobs: false,
            });
        }
    };

    onUndoJobResultChanged = (prevProps) => {
        if (this.props.undoJobResult && this.props.undoJobResult !== prevProps.undoJobResult) {
            if (!this.props.undoJobResult.success) {
                showError("Unable to undo job.");
            }
            this.setState({
                undoJobInProgress: false,
            });
        }
    };

    onIndexDataSetResultChanged = (prevProps) => {
        if (this.props.indexDataSetResult && this.props.indexDataSetResult !== prevProps.indexDataSetResult) {
            if (!this.props.indexDataSetResult.success) {
                showError("Unable to create dataset records index job.");
            } else {
                showSuccess("Index dataset records job created successfully.");
            }
            this.setState({
                busy: false,
                busyMessage: "",
            });
        }
    };

    onExportDataSetToFileResultChanged = (prevProps) => {
        if (this.props.exportDataSetToFileResult && this.props.exportDataSetToFileResult !== prevProps.exportDataSetToFileResult) {
            if (!this.props.exportDataSetToFileResult.success) {
                showError("Unable to create export dataset job.");
            } else {
                this.setState({
                    showExportJobCreatedResult: true,
                });
            }
            this.setState({
                busy: false,
                busyMessage: "",
            });
        }
    };

    onExportDataSetToSystemConnectionResultChanged = (prevProps) => {
        if (this.props.exportDataSetToSystemConnectionResult && this.props.exportDataSetToSystemConnectionResult !== prevProps.exportDataSetToSystemConnectionResult) {
            if (!this.props.exportDataSetToSystemConnectionResult.success) {
                showError("Unable to create export dataset job.");
            } else {
                this.setState({
                    showExportJobToSystemConnectionCreatedResult: true,
                });
            }
            this.setState({
                busy: false,
                busyMessage: "",
            });
        }
    };

    onCreateDataObjectResultChanged = (prevProps) => {
        if (this.props.createDataObjectResult && this.props.createDataObjectResult !== prevProps.createDataObjectResult) {
            if (!this.props.createDataObjectResult.success) {
                if (this.props.createDataObjectResult.code === "PERMISSION_DENIED") {
                    showError("Permission denied!");
                } else if (this.props.createDataObjectResult.code === "DUPLICATE_PRIMARY_KEY") {
                    const pkFieldId = this.props.dataSet?.primaryKeyFieldId || this.props.schemaModel?.fields?.find((f) => f.isPrimaryKey)?.fieldId;
                    const pkFieldName = this.props.dataSet?.primaryKeyFieldName || this.props.schemaModel?.fields?.find((f) => f.isPrimaryKey)?.name || "Primary Key";
                    if (this.state.selectedDataObject && pkFieldId) {
                        this.props.updateDataObjectValidationErrors(this.state.selectedDataObject, [
                            { name: pkFieldId, errors: this.props.createDataObjectResult.message || `A record with this ${pkFieldName} already exists.` },
                        ]);
                    }
                    showError(this.props.createDataObjectResult.message || `Duplicate ${pkFieldName} entry detected. Please use a unique value in the primary key field.`);
                } else if (this.props.createDataObjectResult.code === "PRIMARY_KEY_REQUIRED") {
                    const pkFieldId = this.props.dataSet?.primaryKeyFieldId || this.props.schemaModel?.fields?.find((f) => f.isPrimaryKey)?.fieldId;
                    const pkFieldName = this.props.dataSet?.primaryKeyFieldName || this.props.schemaModel?.fields?.find((f) => f.isPrimaryKey)?.name || "Primary Key";
                    if (this.state.selectedDataObject && pkFieldId) {
                        this.props.updateDataObjectValidationErrors(this.state.selectedDataObject, [{ name: pkFieldId, errors: `${pkFieldName} is required.` }]);
                    }
                    showError(`${pkFieldName} is required but missing.`);
                } else if (this.props.createDataObjectResult.code === "MASKED_VALUE") {
                    // A personal data field holds the mask (#####), copied from a masked record: name it (a new
                    // record's form has no record to carry field errors).
                    const fieldNames = Object.keys(this.props.createDataObjectResult.fields || {}).map(
                        (fieldId) => this.props.schemaModel?.fields?.find((field) => field.fieldId === fieldId)?.name || fieldId
                    );
                    showError(
                        fieldNames.length > 0
                            ? `${fieldNames.join(", ")}: the value is masked (#####). Enter the real value.`
                            : this.props.createDataObjectResult.message || "Personal data fields hold the mask (#####) instead of a value. Enter the real values."
                    );
                } else {
                    showError("Unable to create dataset record.");
                }
            } else {
                this.setState({
                    showDataObjectFormContainer: false,
                });
                if (this.state.selectedDataObject) {
                    let selectedDataObject = this.props.dataObjectsByDataSetIdResult.Items[0];
                    this.setState({
                        selectedDataObject,
                    });
                    if (selectedDataObject.errors && selectedDataObject.errors.length > 0) {
                        showWarning("Record created successfully with warnings.");
                    } else {
                        showSuccess("Record created successfully.");
                    }
                    if (selectedDataObject && selectedDataObject.dataObjectId && this.props.schemaModel && this.props.dataSet) {
                        this.readDataObject(selectedDataObject.dataObjectId);
                    }
                }
            }
            this.setState({
                busy: false,
                busyMessage: "",
            });
        }
    };

    onUpdateDataObjectResultChanged = (prevProps) => {
        if (this.props.updateDataObjectResult && this.props.updateDataObjectResult !== prevProps.updateDataObjectResult) {
            if (!this.props.updateDataObjectResult.success) {
                if (this.props.updateDataObjectResult.code === "PERMISSION_DENIED") {
                    showError("Permission denied!");
                } else if (this.props.updateDataObjectResult.code === "DUPLICATE_PRIMARY_KEY") {
                    const pkFieldId = this.props.dataSet?.primaryKeyFieldId || this.props.schemaModel?.fields?.find((f) => f.isPrimaryKey)?.fieldId;
                    const pkFieldName = this.props.dataSet?.primaryKeyFieldName || this.props.schemaModel?.fields?.find((f) => f.isPrimaryKey)?.name || "Primary Key";
                    if (this.state.selectedDataObject && pkFieldId) {
                        this.props.updateDataObjectValidationErrors(this.state.selectedDataObject, [
                            { name: pkFieldId, errors: this.props.updateDataObjectResult.message || `A record with this ${pkFieldName} already exists.` },
                        ]);
                    }
                    showError(this.props.updateDataObjectResult.message || `Duplicate ${pkFieldName} entry detected. Please use a unique value in the primary key field.`);
                } else if (this.props.updateDataObjectResult.code === "PRIMARY_KEY_REQUIRED") {
                    const pkFieldId = this.props.dataSet?.primaryKeyFieldId || this.props.schemaModel?.fields?.find((f) => f.isPrimaryKey)?.fieldId;
                    const pkFieldName = this.props.dataSet?.primaryKeyFieldName || this.props.schemaModel?.fields?.find((f) => f.isPrimaryKey)?.name || "Primary Key";
                    if (this.state.selectedDataObject && pkFieldId) {
                        this.props.updateDataObjectValidationErrors(this.state.selectedDataObject, [{ name: pkFieldId, errors: `${pkFieldName} is required.` }]);
                    }
                    showError(`${pkFieldName} is required but missing.`);
                } else {
                    showError("Unable to update dataset record.");
                }
            } else {
                if (this.state.selectedDataObject) {
                    let selectedDataObject = this.props.dataObjectsByDataSetIdResult.Items.find(
                        (dataObject) => dataObject.dataObjectId === this.state.selectedDataObject.dataObjectId,
                    );
                    if (selectedDataObject) {
                        if (selectedDataObject.errors && selectedDataObject.errors.length > 0) {
                            showWarning("Record saved successfully with warnings.");
                        } else {
                            showSuccess("Record saved successfully.");
                        }
                        if (selectedDataObject.dataObjectId && this.props.schemaModel && this.props.dataSet) {
                            this.readDataObject(selectedDataObject.dataObjectId);
                        }
                    } else {
                        showSuccess("Record saved successfully.");
                    }
                }
            }
            this.setState({
                busy: false,
                busyMessage: "",
            });
        }
    };

    onDeleteDataObjectResultChanged = (prevProps) => {
        if (this.props.deleteDataObjectResult && this.props.deleteDataObjectResult !== prevProps.deleteDataObjectResult) {
            if (!this.props.deleteDataObjectResult.success) {
                showError("Unable to delete dataset record.");
            } else {
                if (this.state.selectedDataObject) {
                    this.setState({
                        selectedDataObject: null,
                    });
                    showSuccess("Record deleted successfully.");
                }
            }
            this.setState({
                busy: false,
                busyMessage: "",
            });
        }
    };

    onGetDataObjectResultChanged = (prevProps) => {
        if (this.props.getDataObjectResult && this.props.getDataObjectResult !== prevProps.getDataObjectResult) {
            if (this.state.urlDataObjectIdRequested) {
                this.onUrlDataObjectRead();
            }
            if (this.props.getDataObjectResult.success) {
                if (this.state.selectedDataObject && this.props.dataObject && this.state.selectedDataObject.dataObjectId === this.props.dataObject.dataObjectId) {
                    this.setState({
                        selectedDataObject: this.props.dataObject,
                        loadingDataObjectForm: false,
                    });
                }
            }
        }
    };

    // The open record updated by the server (a pushed update, which leaves getDataObjectResult as it
    // is): the form shows it, as it does a read of the same record.
    onPushedDataObjectChanged = (prevProps) => {
        if (
            this.props.dataObject &&
            this.props.dataObject !== prevProps.dataObject &&
            this.props.getDataObjectResult === prevProps.getDataObjectResult &&
            this.state.selectedDataObject &&
            this.state.selectedDataObject.dataObjectId === this.props.dataObject.dataObjectId
        ) {
            this.setState({ selectedDataObject: this.props.dataObject });
        }
    };

    onGetDeDuplicatedRecordsRequestResultChanged = (prevProps) => {
        if (this.props.getDeDuplicatedRecordsRequestResult && this.props.getDeDuplicatedRecordsRequestResult !== prevProps.getDeDuplicatedRecordsRequestResult) {
            if (!this.props.getDeDuplicatedRecordsRequestResult.success) {
                showError("Unable to get deduplicated records.");
            }
            this.setState({
                fetchingDeDuplicatedRecords: false,
            });
        }
    };

    getDataSetObjects = () => {
        this.setState({
            fetchingDataObjects: true,
        });
        this.props.getDataObjectsByDataSetId(this.props.schemaModel.businessAreaId, this.props.schemaModel.schemaId, this.props.dataSet.dataSetId);
    };

    loadingComponentData = () => {
        return this.state.fetchingDataSet || this.state.fetchingDataSetPermission || this.state.fetchingSchemaDataSets || this.state.fetchingCommentsPermission;
    };

    isBusy = () => {
        let isBusy = this.state.busy || this.loadingComponentData();
        return isBusy;
    };

    setDataSetPermissions = (permission, userBusinessArea) => {
        let dataSetPermission = {
            canAdd: false,
            canView: false,
            canEdit: false,
            canDelete: false,
        };
        if (permission && userBusinessArea && userBusinessArea.user) {
            dataSetPermission = {
                canAdd: permission.canAdd && userBusinessArea.user.canAdd,
                canView: permission.canView && userBusinessArea.user.canView,
                canEdit: permission.canEdit && userBusinessArea.user.canEdit,
                canDelete: permission.canDelete && userBusinessArea.user.canDelete,
            };
        }
        this.setState({
            dataSetPermission,
        });
    };

    showReviewQueue = () => {
        this.props.history.push(
            AppPaths.TENANT_SCHEMA_REVIEW_QUEUE.replace(":tenant", this.props.tenant.toLowerCase())
                .replace(":businessarea", this.props.businessArea.toLowerCase())
                .replace(":schema", this.props.selectedSchemaModel.name),
        );
    };

    showCreateDataSet = () => {
        this.props.history.push(
            AppPaths.TENANT_SCHEMA_DATASET_ACTION.replace(":tenant", this.props.tenant.toLowerCase())
                .replace(":businessarea", this.props.businessArea.toLowerCase())
                .replace(":schema", this.props.selectedSchemaModel.name)
                .replace(":dataSetAction", "create"),
        );
    };

    showEditDataSet = (dataSetId) => {
        this.props.history.push(
            AppPaths.TENANT_SCHEMA_DATASET_ID_ACTION.replace(":tenant", this.props.tenant.toLowerCase())
                .replace(":businessarea", this.props.businessArea.toLowerCase())
                .replace(":schema", this.props.selectedSchemaModel.name)
                .replace(":dataSetId", dataSetId)
                .replace(":dataSetAction", "edit"),
        );
    };

    onSwitchEditMode = (dataSet) => {
        this.props.history.push(
            AppPaths.TENANT_SCHEMA_DATASET_ID_ACTION.replace(":tenant", this.props.tenant.toLowerCase())
                .replace(":businessarea", this.props.businessArea.toLowerCase())
                .replace(":schema", dataSet.schemaName)
                .replace(":dataSetId", dataSet.dataSetId)
                .replace(":dataSetAction", "edit"),
        );
    };

    showViewDataSet = (dataSetId) => {
        this.props.history.push(
            AppPaths.TENANT_SCHEMA_DATASET_ID_ACTION.replace(":tenant", this.props.tenant.toLowerCase())
                .replace(":businessarea", this.props.businessArea.toLowerCase())
                .replace(":schema", this.props.selectedSchemaModel.name)
                .replace(":dataSetId", dataSetId)
                .replace(":dataSetAction", "view"),
        );
    };

    showDataSetHistory = (dataSetId) => {
        this.props.history.push(
            AppPaths.TENANT_SCHEMA_DATASET_ID_ACTION.replace(":tenant", this.props.tenant.toLowerCase())
                .replace(":businessarea", this.props.businessArea.toLowerCase())
                .replace(":schema", this.props.selectedSchemaModel.name)
                .replace(":dataSetId", dataSetId)
                .replace(":dataSetAction", "history"),
        );
    };

    showDataObjectBulkUpload = (dataSetName, source) => {
        this.props.history.push(
            `${AppPaths.TENANT_DATASET_DATAOBJECT_ACTION.replace(":tenant", this.props.tenant.toLowerCase())
                .replace(":businessarea", this.props.businessArea.toLowerCase())
                .replace(":schema", this.props.schema)
                .replace(":dataset", dataSetName)
                .replace(":dataObjectAction", "bulkupload")}#${source}`,
            {
                dataSetAction: this.props.action,
            },
        );
    };

    onIndexDataSet = (dataSet) => {
        this.setState({
            dataSetToIndex: dataSet,
            showIndexDataSetConfirmation: true,
            indexDataSetConfirmMessage: `Are you sure, you want to index '${dataSet.dataSetName}' Dataset records? Indexing would take time depending on the size of the dataset.`,
        });
    };

    onIndexDataSetConfirmed = () => {
        this.props.indexDataSet(this.state.dataSetToIndex);
        this.setState({
            dataSetToIndex: null,
            showIndexDataSetConfirmation: false,
            busy: true,
            busyMessage: "Creating index dataset records job...",
        });
    };

    onIndexDataSetCancel = () => {
        this.setState({
            dataSetToIndex: null,
            showIndexDataSetConfirmation: false,
        });
    };

    onExportDataSetToFile = (dataSet) => {
        this.setState({
            busy: true,
            busyMessage: "Creating export dataset job...",
        });
        this.props.exportDataSetToFile(dataSet);
    };

    onExportDataSetToSystemConnectionClick = (dataSet) => {
        this.props.history.push(
            AppPaths.TENANT_SCHEMA_DATASET_ID_ACTION.replace(":tenant", this.props.tenant.toLowerCase())
                .replace(":businessarea", this.props.businessArea.toLowerCase())
                .replace(":schema", this.props.selectedSchemaModel.name)
                .replace(":dataSetId", dataSet.dataSetId)
                .replace(":dataSetAction", "export"),
        );
    };

    onExportDataSetToSystemConnection = (connection) => {
        this.setState({
            busy: true,
            busyMessage: "Creating export dataset job...",
        });
        this.props.exportDataSetToSystemConnection(this.props.dataSet, connection);
    };

    onExportDataSetToSystemConnectionCancel = () => {
        this.props.history.push(
            `${AppPaths.TENANT_BUSINESS_AREA_SCHEMA.replace(":tenant", this.props.tenant.toLowerCase()).replace(
                ":businessarea",
                this.props.businessArea.toLowerCase(),
            )}${this.props.schemaModel ? `#${this.props.schemaModel.name}` : ""}`,
        );
    };

    closeExportJobCreatedResult = () => {
        this.setState({
            showExportJobCreatedResult: false,
        });
    };

    closeExportJobToSystemConnectionCreatedResult = () => {
        this.props.history.push(
            `${AppPaths.TENANT_BUSINESS_AREA_SCHEMA.replace(":tenant", this.props.tenant.toLowerCase()).replace(
                ":businessarea",
                this.props.businessArea.toLowerCase(),
            )}${this.props.schemaModel ? `#${this.props.schemaModel.name}` : ""}`,
        );
    };

    showDataObjectHistory = (dataSetName, dataObjectId) => {
        this.props.history.push(
            AppPaths.TENANT_DATASET_DATAOBJECT_ID_ACTION.replace(":tenant", this.props.tenant.toLowerCase())
                .replace(":businessarea", this.props.businessArea.toLowerCase())
                .replace(":schema", this.props.schema)
                .replace(":dataset", dataSetName)
                .replace(":dataObjectId", dataObjectId)
                .replace(":dataObjectAction", "history"),
        );
    };

    goToSchemas = () => {
        this.props.history.push(
            `${AppPaths.TENANT_BUSINESS_AREA_SCHEMA.replace(":tenant", this.props.tenant.toLowerCase()).replace(
                ":businessarea",
                this.props.businessArea.toLowerCase(),
            )}${this.props.schemaModel ? `#${this.props.schemaModel.name}` : ""}`,
        );
    };

    createDataSet = (dataSet, afterSaveCallback) => {
        this.setState({
            busy: true,
            busyMessage: "Creating dataset...",
            afterSaveCallback,
        });
        this.props.createDataSet(this.props.schemaModel.businessAreaId, this.props.schemaModel.schemaId, dataSet);
    };

    updateDataSet = (dataSet, updatedDataSet, afterSaveCallback) => {
        this.setState({
            busy: true,
            busyMessage: "Updating dataset...",
            afterSaveCallback,
        });
        this.props.updateDataSet(this.props.schemaModel.businessAreaId, this.props.schemaModel.schemaId, dataSet, updatedDataSet);
    };

    showDeleteDataSetConfirmation = (dataSet) => {
        this.setState({
            deletedDataSet: dataSet,
            showDataSetDeleteConfirmation: true,
            deleteDataSetConfirmMessage: `Are you sure, you want to delete '${dataSet.dataSetName}' Dataset?`,
        });
    };

    onDeleteDataSetConfirmed = () => {
        this.setState({
            busy: true,
            busyMessage: "Deleting dataset...",
        });
        this.props.deleteDataSet(this.props.selectedSchemaModel.businessAreaId, this.props.selectedSchemaModel.schemaId, this.state.deletedDataSet.dataSetId);
        this.onDeleteDataSetCancel();
    };

    onDeleteDataSetCancel = () => {
        this.setState({
            deletedDataSet: null,
            showDataSetDeleteConfirmation: false,
            deleteDataSetConfirmMessage: "",
        });
    };

    onDataSetValuesChanged = (changedValues, allValues) => {
        if (_.isEmpty(this.state.manageDataSetErrors) === false) {
            let manageDataSetErrors = { ...this.state.manageDataSetErrors };
            let flatObject = flatten(changedValues);
            for (let key in flatObject) {
                delete manageDataSetErrors[key];
            }
            this.setState({
                manageDataSetErrors,
            });
        }
    };

    getEmptyText = () => {
        if (!this.props.selectedSchemaModel) {
            return <EmptyDataRow description="Please select a schema."></EmptyDataRow>;
        }
        if (this.props.selectedSchemaModel) {
            if (this.state.fetchingSchemaDataSets) {
                return <EmptyDataRow></EmptyDataRow>;
            }
            return <EmptyDataRow description={`There are no data sets for schema - ${this.props.selectedSchemaModel.name}`}></EmptyDataRow>;
        }
        return "";
    };

    getNotFoundError = () => {
        if (!this.props.dataSet) {
            if (this.props.getDataSetByIdResult.success) {
                return {
                    title: "Dataset not found.",
                    description: "The dataset you are looking for does not exist.",
                };
            }
            return {
                title: "Dataset could not be fetched.",
                description: "An error occurred while fetching dataset. Please contact support.",
            };
        }
        return null;
    };

    getNextDataObjects = (dataSetId, pageKey) => {
        this.setState({
            fetchingNextPageDataObjects: true,
        });
        this.props.getDataObjectsByDataSetIdNextPage(this.props.schemaModel.businessAreaId, this.props.schemaModel.schemaId, dataSetId, pageKey);
    };

    getDataSetJobs = (dataSetId) => {
        this.props.getDataSetJobs(dataSetId);
        this.setState({
            fetchingDataSetJobs: true,
        });
    };

    onUndoJob = (jobId, version) => {
        this.setState({
            undoJobInProgress: true,
        });
        this.props.undoJob(jobId, version);
    };

    onDataObjectSelected = (dataObject) => {
        this.props.getDeDuplicatedRecords(dataObject.dataObjectId);
        this.readDataObject(dataObject.dataObjectId);
        this.setState({
            selectedDataObject: dataObject,
            loadingDataObjectForm: true,
            fetchingDeDuplicatedRecords: true,
            showDataObjectFormContainer: true,
        });
    };

    onCreateNewDataObject = () => {
        this.setState({
            selectedDataObject: null,
            showDataObjectFormContainer: true,
        });
    };

    openCommentsForDataObject = (dataObject) => {
        this.onDataObjectSelected(dataObject);
        this.setState({ sidePanelActiveTab: "COMMENTS", showDataObjectFormContainer: true });
    };

    openAnnotationsForDataObject = (dataObject) => {
        this.onDataObjectSelected(dataObject);
        this.setState({ sidePanelActiveTab: "ANNOTATIONS", showDataObjectFormContainer: true });
    };

    createDataObject = (dataObject) => {
        this.setState({
            busy: true,
            busyMessage: "Creating dataset record...",
        });
        this.props.createDataObject(this.props.schemaModel.businessAreaId, this.props.schemaModel.schemaId, this.props.dataSet.dataSetId, dataObject);
    };

    updateDataObject = (dataObjectId, dataObject) => {
        this.setState({
            busy: true,
            busyMessage: "Updating dataset record...",
        });
        this.props.updateDataObject(this.props.schemaModel.businessAreaId, this.props.schemaModel.schemaId, this.props.dataSet.dataSetId, dataObjectId, dataObject);
    };

    showDeleteDataObjectConfirmation = (dataObjectId) => {
        this.setState({
            deletedDataObjectId: dataObjectId,
            showDataObjectDeleteConfirmation: true,
            deleteDataObjectConfirmMessage: `Are you sure, you want to delete dataset record?`,
        });
    };

    onDeleteDataObjectConfirmed = () => {
        this.setState({
            busy: true,
            busyMessage: "Deleting dataset record...",
        });
        this.props.deleteDataObject(this.props.schemaModel.businessAreaId, this.props.schemaModel.schemaId, this.props.dataSet.dataSetId, this.state.deletedDataObjectId);
        this.onDeleteDataObjectCancel();
    };

    onDeleteDataObjectCancel = () => {
        this.setState({
            deletedDataObjectId: null,
            showDataObjectDeleteConfirmation: false,
            deleteDataObjectConfirmMessage: "",
        });
    };

    closeDataObjectForm = () => {
        this.setState({
            selectedDataObject: null,
            showDataObjectFormContainer: false, // Hide side panel when no data is selected
        });
    };

    onValidationErrors = (dataObject, validationErrors) => {
        this.props.updateDataObjectValidationErrors(dataObject, validationErrors);
    };

    goToJobs = () => {
        this.props.history.push(AppPaths.TENANT_MONITOR_ITEM.replace(":tenant", this.props.tenant.toLowerCase()).replace(":monitorItem", "jobs"));
    };

    onSearchDataObjects = (query) => {
        if (!query) {
            this.props.clearSearchDataSetDataObjects();
            return;
        }
        this.props.searchDataSetDataObjects(this.props.schemaModel.businessAreaId, this.props.schemaModel.schemaId, this.props.dataSet.dataSetId, query);
    };

    onSidePanelActiveTabChanged = (activeKey) => {
        this.setState({
            sidePanelActiveTab: activeKey,
        });
    };

    handleAttachmentRemove = (file) => {
        // TODO: Implement attachment removal logic
        console.log("Remove attachment:", file);
    };

    handleAttachmentDownload = (file) => {
        // TODO: Implement attachment download logic
        console.log("Download attachment:", file);
    };
    renderLocationContainer = () => {
        const result = resolveLocation(this.props.schemaModel?.fields || [], this.state.selectedDataObject);
        // console.log("Resolved location result:", result);

        const commonProps = {
            businessAreaId: this.props.schemaModel?.businessAreaId,
            schemaId: this.props.schemaModel?.schemaId,
            dataSetId: this.props.dataSet?.dataSetId,
            selectedDataObject: this.state.selectedDataObject, // Always pass full object for metadata hydration/saving
        };

        if (result.type === "markers") {
            return <LocationContainer markers={result.data} {...commonProps} />;
        }

        if (result.type === "address") {
            return <LocationContainer locationAddress={result.data} {...commonProps} />;
        }

        const alertTypeMap = {
            MASKED_DATA: "info",
            EMPTY_VALUE: "warning",
            INVALID_COORDINATES: "error",
            NULL_ISLAND: "warning",
            JUNK_VALUE: "warning",
            INSUFFICIENT_PARTS: "warning",
            NO_FIELDS: "info",
        };

        return <Alert type={alertTypeMap[result.error.code] || "warning"} message={result.error.message} showIcon style={{ margin: "20px" }} />;
    };

    getDataSetComponent = () => {
        if (!this.props.action) {
            return (
                <>
                    <SuccessResult
                        show={this.state.showExportJobCreatedResult}
                        title="Export dataset to a file job created!"
                        subTitle="A job is created. File will be available in the job for download once job is completed."
                        okText="Go to jobs"
                        cancelText="Close"
                        onOkClick={this.goToJobs}
                        onCancelClick={this.closeExportJobCreatedResult}
                    ></SuccessResult>
                    <SuccessResult
                        show={this.state.showExportJobToSystemConnectionCreatedResult}
                        title="Export dataset to a system connection job created!"
                        subTitle="A job is created. Dataset records would be exported to selected destination once job is completed."
                        okText="Go to jobs"
                        cancelText="Close"
                        onOkClick={this.goToJobs}
                        onCancelClick={this.closeExportJobToSystemConnectionCreatedResult}
                    ></SuccessResult>
                    <OkCancelConfirmationBox
                        show={this.state.showDataSetDeleteConfirmation}
                        message={this.state.deleteDataSetConfirmMessage}
                        onCancel={this.onDeleteDataSetCancel}
                        onOk={this.onDeleteDataSetConfirmed}
                    ></OkCancelConfirmationBox>
                    <OkCancelConfirmationBox
                        show={this.state.showIndexDataSetConfirmation}
                        message={this.state.indexDataSetConfirmMessage}
                        onCancel={this.onIndexDataSetCancel}
                        onOk={this.onIndexDataSetConfirmed}
                    ></OkCancelConfirmationBox>
                    <DataSetList
                        permission={this.props.selectedSchemaModel ? this.state.dataSetPermission : {}}
                        allowCreateNew={this.props.selectedSchemaModel && !this.state.fetchingSchemaDataSets ? true : false}
                        dataSetList={this.state.fetchingSchemaDataSets || !this.props.selectedSchemaModel ? [] : this.props.schemaDataSetResult.Items}
                        parentSchema={this.props.selectedSchemaModel && this.props.selectedSchemaModel.name}
                        emptyText={this.getEmptyText()}
                        onCreateNewDataSet={this.showCreateDataSet}
                        onEditDataSet={this.showEditDataSet}
                        onDeleteDataSet={this.showDeleteDataSetConfirmation}
                        onViewDataSet={this.showViewDataSet}
                        onViewDataSetHistory={this.showDataSetHistory}
                        onIndexDataSet={this.onIndexDataSet}
                        onExportToFileClick={this.onExportDataSetToFile}
                        onExportToSystemConnectionClick={this.onExportDataSetToSystemConnectionClick}
                        exportPermission={this.props.exportPermission}
                        headerExtra={
                            this.props.selectedSchemaModel ? (
                                <ReviewQueueButton
                                    businessAreaId={this.props.selectedSchemaModel.businessAreaId}
                                    schemaId={this.props.selectedSchemaModel.schemaId}
                                    onOpen={this.showReviewQueue}
                                />
                            ) : undefined
                        }
                    ></DataSetList>
                </>
            );
        }
        let notFoundError = this.getNotFoundError();

        const recordKeys = {
            DATASET_RECORD: "Dataset Record",
            ATTACHMENTS: "Attachments",
            DEDUPLICATION_HISTORY: "Deduplication History",
            DATA_OBJECT_VERIFICATION: "Verification",
            DATA_OBJECT_CONSENT: "Consent",
            LOCATION_ACTIVE: "Location",
        };

        const shouldInjectAnnotationFilters =
            this.state.annotationFiltersFromUrl &&
            !this.state.annotationFiltersApplied &&
            this.state.selectedDataObject &&
            this.state.selectedDataObject.dataObjectId === this.state.annotationFiltersRecordId;
        const annotationsInitialFilters = shouldInjectAnnotationFilters ? this.state.annotationFiltersFromUrl : null;

        switch (this.props.action.toLowerCase()) {
            case "create":
                return (
                    <ManageDataSet
                        openComments={this.state.openDatasetCommentsFromUrl}
                        permission={this.state.dataSetPermission}
                        schemaModel={this.props.schemaModel}
                        formErrors={this.state.manageDataSetErrors}
                        onValuesChanged={this.onDataSetValuesChanged}
                        action={this.props.action}
                        onBulkUploadData={this.showDataObjectBulkUpload}
                        onCreateDataSet={this.createDataSet}
                        onCancel={this.goToSchemas}
                        onOpenLocationForDataObject={(dataObject) => {
                            this.onDataObjectSelected(dataObject);
                            this.setState({ sidePanelActiveTab: "LOCATION_ACTIVE", showDataObjectFormContainer: true });
                        }}
                        onVerifyEmailForDataObject={(dataObject) => {
                            this.onDataObjectSelected(dataObject);
                            this.setState({ sidePanelActiveTab: "DATA_OBJECT_VERIFICATION", showDataObjectFormContainer: true });
                        }}
                    ></ManageDataSet>
                );
            case "view":
            case "edit":
                if (this.props.userCommentsPermission && this.props.userCommentsPermission.canView) {
                    recordKeys.COMMENTS = "Comments";
                    recordKeys.ANNOTATIONS = "Annotations";
                }

                return !notFoundError ? (
                    <div className="flex size-full flex-auto gap-4 overflow-auto">
                        <OkCancelConfirmationBox
                            show={this.state.showDataObjectDeleteConfirmation}
                            message={this.state.deleteDataObjectConfirmMessage}
                            onCancel={this.onDeleteDataObjectCancel}
                            onOk={this.onDeleteDataObjectConfirmed}
                        ></OkCancelConfirmationBox>
                        <ManageDataSet
                            openComments={this.state.openDatasetCommentsFromUrl}
                            permission={this.state.dataSetPermission}
                            schemaModel={this.props.schemaModel}
                            dataSet={this.props.dataSet}
                            formErrors={this.state.manageDataSetErrors}
                            onValuesChanged={this.onDataSetValuesChanged}
                            action={this.props.action}
                            onBulkUploadData={this.showDataObjectBulkUpload}
                            onUpdateDataSet={this.updateDataSet}
                            loadingDataObjects={this.state.fetchingDataObjects || this.props.searchDataObjectsLoading}
                            loadingNextPageDataObjects={this.state.fetchingNextPageDataObjects}
                            dataObjectsResult={this.props.dataObjectsByDataSetIdResult}
                            dataObjects={this.props.dataObjectsByDataSetIdResult.Items}
                            onGetNextDataObjects={this.getNextDataObjects}
                            onCancel={this.goToSchemas}
                            onGetBatchUploadJobs={this.getDataSetJobs}
                            fetchingBatchUploadJobs={this.state.fetchingDataSetJobs}
                            jobList={this.props.jobsResult.Items}
                            undoJob={this.onUndoJob}
                            undoJobInProgress={this.state.undoJobInProgress}
                            refreshDataObjects={this.getDataSetObjects}
                            onDataObjectSelected={this.onDataObjectSelected}
                            onCreateNewDataObject={this.onCreateNewDataObject}
                            onShowDataObjectHistory={this.showDataObjectHistory}
                            onDeleteDataObject={this.showDeleteDataObjectConfirmation}
                            bulkVerification={true}
                            onSwitchEditMode={this.onSwitchEditMode}
                            selectedDataObject={this.state.selectedDataObject}
                            onSearch={this.onSearchDataObjects}
                            searchResultDataObjects={this.props.searchDataObjectsItems}
                            onOpenPipelineBuilder={() => this.setState({ pipelineBuilderVisible: true })}
                            onOpenLocationForDataObject={(dataObject) => {
                                this.onDataObjectSelected(dataObject);
                                this.setState({ sidePanelActiveTab: "LOCATION_ACTIVE", showDataObjectFormContainer: true });
                            }}
                            onOpenCommentsForDataObject={this.openCommentsForDataObject}
                            onOpenAnnotationsForDataObject={this.openAnnotationsForDataObject}
                            onVerifyEmailForDataObject={(dataObject) => {
                                this.onDataObjectSelected(dataObject);
                                this.setState({ sidePanelActiveTab: "DATA_OBJECT_VERIFICATION", showDataObjectFormContainer: true });
                            }}
                        ></ManageDataSet>
                        <PipelineBuilder
                            visible={this.state.pipelineBuilderVisible}
                            onClose={() => {
                                this.setState({ pipelineBuilderVisible: false, initialPipelineId: null });
                            }}
                            initialPipelineId={this.state.initialPipelineId}
                            onInitialPipelineLoaded={() => this.setState({ initialPipelineId: null })}
                            datasetId={this.props.dataSet?.dataSetId}
                            datasetName={this.props.dataSet?.dataSetName}
                            schemaId={this.props.schemaModel?.schemaId}
                            businessAreaId={this.props.schemaModel?.businessAreaId}
                            columns={this.props.schemaModel?.fields?.map((a) => a.name) || []}
                            schemaFields={this.props.schemaModel?.fields || []}
                            pipelines={this.props.pipelinesResult}
                            onCreatePipeline={(data) => this.props.createPipeline(data)}
                            onUpdatePipeline={(id, data) => this.props.updatePipeline(id, data)}
                            onDeletePipeline={(id) => this.props.deletePipeline(id)}
                            onGetPipelines={(datasetId) => this.props.getPipelines(datasetId)}
                            onDryRunPipelineInline={(datasetId, steps) => this.props.dryRunPipelineInline(datasetId, steps)}
                            createPipelineResult={this.props.createPipelineResult}
                            updatePipelineResult={this.props.updatePipelineResult}
                            deletePipelineResult={this.props.deletePipelineResult}
                            dryRunResult={this.props.dryRunResult}
                            dryRunLoading={this.props.dryRunLoading}
                            views={this.props.viewsListResult}
                            onCreateView={(data) => this.props.createView(data)}
                            onUpdateView={(id, data) => this.props.updateView(id, data)}
                            onDeleteView={(id) => this.props.deleteView(id)}
                            onRunView={(id) => this.props.runView(id)}
                            onRunPipeline={(id) => this.props.runPipeline(id)}
                            runPipelineResult={this.props.runPipelineResult}
                            onGetViewData={(id) => this.props.getViewData(id)}
                            onGetViews={(filters) => this.props.getViewsList(filters)}
                            createViewResult={this.props.createViewResult}
                            updateViewResult={this.props.updateViewResult}
                            deleteViewResult={this.props.deleteViewResult}
                            runViewResult={this.props.runViewResult}
                            viewData={this.props.viewData}
                            viewDataLoading={this.props.viewDataLoading}
                            onGoToJobs={this.goToJobs}
                        />
                        {!!this.state.showDataObjectFormContainer && (
                            <SidePanel>
                                <MenuPanelContainer recordKeys={recordKeys} selectedKey={this.state.sidePanelActiveTab} onChange={this.onSidePanelActiveTabChanged}>
                                    {this.state.sidePanelActiveTab === "DATASET_RECORD" &&
                                        (this.state.loadingDataObjectForm ? (
                                            <LoadingOverlay busy={true} message="Please wait..."></LoadingOverlay>
                                        ) : (
                                            <BasicFormBuilder
                                                key={this.state.selectedDataObject ? this.state.selectedDataObject.dataObjectId : "new_dataobject"}
                                                showHeader={false}
                                                formDataObject={this.state.selectedDataObject}
                                                schemaFields={(this.props.schemaModel && this.props.schemaModel.fields) || []}
                                                onCreate={this.createDataObject}
                                                onUpdate={this.updateDataObject}
                                                onCancel={this.closeDataObjectForm}
                                                onValidationErrors={this.onValidationErrors}
                                                requestVerification={true}
                                                showRevealButton={true}
                                                readOnly={!this.props.dataObjectPermission?.canEdit || this.props.action === "view"}
                                            ></BasicFormBuilder>
                                        ))}
                                    {this.state.sidePanelActiveTab === "ATTACHMENTS" &&
                                        (this.state.selectedDataObject ? (
                                            <AttachmentTab
                                                dataObject={this.state.selectedDataObject}
                                                schemaFields={(this.props.schemaModel && this.props.schemaModel.fields) || []}
                                                onDownload={this.handleAttachmentDownload}
                                            />
                                        ) : (
                                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Choose a row to view attachments." />
                                        ))}
                                    {this.state.sidePanelActiveTab === "DEDUPLICATION_HISTORY" &&
                                        (this.state.fetchingDeDuplicatedRecords ? (
                                            <LoadingOverlay busy={true} message="Getting records..."></LoadingOverlay>
                                        ) : (
                                            <DeduplicatedRecordsTable
                                                items={
                                                    this.state.selectedDataObject && this.state.selectedDataObject.dataObjectId ? this.props.deDuplicatedRecordsResult.Items : []
                                                }
                                            ></DeduplicatedRecordsTable>
                                        ))}
                                    {this.state.sidePanelActiveTab === "DATA_OBJECT_VERIFICATION" && this.state.selectedDataObject && (
                                        <DataObjectVerifications
                                            key={this.state.selectedDataObject.dataObjectId}
                                            businessAreaId={this.state.selectedDataObject.businessAreaId}
                                            schemaId={this.state.selectedDataObject.schemaId}
                                            dataObjectId={this.state.selectedDataObject.dataObjectId}
                                        ></DataObjectVerifications>
                                    )}
                                    {this.state.sidePanelActiveTab === "DATA_OBJECT_CONSENT" &&
                                        (this.state.sidePanelActiveTab === "DATA_OBJECT_CONSENT" ? (
                                            this.state.selectedDataObject && (
                                                <DataObjectConsents
                                                    key={this.state.selectedDataObject.dataObjectId}
                                                    dataObjectId={this.state.selectedDataObject.dataObjectId}
                                                ></DataObjectConsents>
                                            )
                                        ) : (
                                            <></>
                                        ))}
                                    {this.state.sidePanelActiveTab === "LOCATION_ACTIVE" &&
                                        (this.state.selectedDataObject ? (
                                            this.renderLocationContainer()
                                        ) : (
                                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Choose a row to view location details." />
                                        ))}
                                    {this.props.userCommentsPermission &&
                                        this.props.userCommentsPermission.canView &&
                                        this.state.sidePanelActiveTab === "COMMENTS" &&
                                        this.state.selectedDataObject && (
                                            <Comments
                                                key={this.state.selectedDataObject.dataObjectId}
                                                recordId={this.state.selectedDataObject.dataObjectId}
                                                permission={this.props.userCommentsPermission}
                                                recordType="dataobject"
                                            ></Comments>
                                        )}
                                    {this.props.userCommentsPermission &&
                                        this.props.userCommentsPermission.canView &&
                                        this.state.sidePanelActiveTab === "ANNOTATIONS" &&
                                        (this.state.selectedDataObject ? (
                                            <Annotations
                                                key={this.state.selectedDataObject.dataObjectId}
                                                recordId={this.state.selectedDataObject.dataObjectId}
                                                recordType="dataRecord"
                                                permission={this.props.userCommentsPermission}
                                                fields={this.props.schemaModel?.fields || []}
                                                businessAreaId={this.props.schemaModel?.businessAreaId}
                                                initialServerFilters={annotationsInitialFilters}
                                                historyDisplayMode="modal"
                                            ></Annotations>
                                        ) : (
                                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Choose a row to view annotations." />
                                        ))}
                                </MenuPanelContainer>
                            </SidePanel>
                        )}
                    </div>
                ) : (
                    <NotFoundError title={notFoundError.title} description={notFoundError.description}></NotFoundError>
                );
            case "history":
                return !notFoundError ? (
                    this.state.showHistory ? (
                        <DataSetAudit dataSet={this.props.dataSet}></DataSetAudit>
                    ) : (
                        <></>
                    )
                ) : (
                    <NotFoundError title={notFoundError.title} description={notFoundError.description}></NotFoundError>
                );
            case "export":
                return !notFoundError ? (
                    <>
                        <SuccessResult
                            show={this.state.showExportJobToSystemConnectionCreatedResult}
                            title="Export dataset to a system connection job created!"
                            subTitle="A job is created. Dataset records would be exported to selected destination once job is completed."
                            okText="Go to jobs"
                            cancelText="Close"
                            onOkClick={this.goToJobs}
                            onCancelClick={this.closeExportJobToSystemConnectionCreatedResult}
                        ></SuccessResult>
                        <DataSetExport
                            businessAreaId={this.props.schemaModel.businessAreaId}
                            onExport={this.onExportDataSetToSystemConnection}
                            onCancel={this.onExportDataSetToSystemConnectionCancel}
                        ></DataSetExport>
                    </>
                ) : (
                    <NotFoundError title={notFoundError.title} description={notFoundError.description}></NotFoundError>
                );
            default:
                return <ProhibitedArea></ProhibitedArea>;
        }
    };

    render() {
        if (this.state.permissionDenied) {
            return <ProhibitedArea></ProhibitedArea>;
        }
        return (
            <div className="relative flex flex-1 flex-col overflow-auto">
                <LoadingOverlay busy={this.isBusy()} spinner message={this.state.busyMessage || "Please wait..."}></LoadingOverlay>
                {this.state.mergedRecordNotice && (
                    <MergedRecordNotice
                        message={this.state.mergedRecordNotice.message}
                        onOpenSurvivor={this.mergedRecordNoticeLinks().survivorId && this.props.dataSet ? this.openSurvivorRecord : undefined}
                        subsumedPath={this.mergedRecordNoticeLinks().subsumedPath}
                        onClose={() => this.setState({ mergedRecordNotice: null })}
                    />
                )}
                {!this.loadingComponentData() && this.getDataSetComponent()}
            </div>
        );
    }
}

DataSets.propTypes = {
    action: PropTypes.string,
    dataSetId: PropTypes.string,
    schemaModel: PropTypes.object,
    selectedSchemaModel: PropTypes.object,
    businessArea: PropTypes.string,
    tenant: PropTypes.string,
    schema: PropTypes.string,
    dataSet: PropTypes.object,
    location: PropTypes.object,
    history: PropTypes.object,
    setBreadcrumbNavigationItems: PropTypes.func,
    userBusinessArea: PropTypes.object,
    dataSetPermission: PropTypes.object,
    dataObjectPermission: PropTypes.object,
    userCommentsPermission: PropTypes.object,
    exportPermission: PropTypes.object,
    getDataObjectPermissionResult: PropTypes.object,
    getCommentsPermissionResult: PropTypes.object,
    getUserDataSetPermissionResult: PropTypes.object,
    getSchemaDataSetResult: PropTypes.object,
    schemaDataSetResult: PropTypes.object,
    recentDataSetListResult: PropTypes.object,
    createDataSetResult: PropTypes.object,
    getDataSetByIdResult: PropTypes.object,
    updateDataSetResult: PropTypes.object,
    deleteDataSetResult: PropTypes.object,
    dataObjectsByDataSetIdResult: PropTypes.object,
    getDataObjectsByDataSetIdResult: PropTypes.object,
    getDataObjectsByDataSetIdNextPageResult: PropTypes.object,
    getJobsResult: PropTypes.object,
    jobsResult: PropTypes.object,
    undoJobResult: PropTypes.object,
    createDataObjectResult: PropTypes.object,
    updateDataObjectResult: PropTypes.object,
    deleteDataObjectResult: PropTypes.object,
    getDataObjectResult: PropTypes.object,
    dataObject: PropTypes.object,
    indexDataSetResult: PropTypes.object,
    exportDataSetToFileResult: PropTypes.object,
    exportDataSetToSystemConnectionResult: PropTypes.object,
    getDeDuplicatedRecordsRequestResult: PropTypes.object,
    deDuplicatedRecordsResult: PropTypes.object,
    getDataObjectPermission: PropTypes.func,
    getCommentsPermission: PropTypes.func,
    getUserDataSetPermission: PropTypes.func,
    getDataSetBySchemaId: PropTypes.func,
    createDataSet: PropTypes.func,
    getDataSetById: PropTypes.func,
    updateDataSet: PropTypes.func,
    deleteDataSet: PropTypes.func,
    getDataObjectsByDataSetId: PropTypes.func,
    getDataObjectsByDataSetIdNextPage: PropTypes.func,
    getDataObject: PropTypes.func,
    getDataSetJobs: PropTypes.func,
    undoJob: PropTypes.func,
    createDataObject: PropTypes.func,
    updateDataObject: PropTypes.func,
    updateDataObjectValidationErrors: PropTypes.func,
    deleteDataObject: PropTypes.func,
    indexDataSet: PropTypes.func,
    exportDataSetToFile: PropTypes.func,
    exportDataSetToSystemConnection: PropTypes.func,
    getUserExportJobPermission: PropTypes.func,
    getDeDuplicatedRecords: PropTypes.func,
    searchDataObjectsResult: PropTypes.object,
    searchDataObjectsLoading: PropTypes.bool,
    searchDataObjectsItems: PropTypes.array,
    searchDataSetDataObjects: PropTypes.func,
    clearSearchDataSetDataObjects: PropTypes.func,
    pipelinesResult: PropTypes.object,
    createPipelineResult: PropTypes.object,
    updatePipelineResult: PropTypes.object,
    deletePipelineResult: PropTypes.object,
    dryRunResult: PropTypes.object,
    dryRunLoading: PropTypes.bool,
    getPipelines: PropTypes.func,
    dryRunPipelineInline: PropTypes.func,
    createPipeline: PropTypes.func,
    updatePipeline: PropTypes.func,
    deletePipeline: PropTypes.func,
    viewsListResult: PropTypes.object,
    createViewResult: PropTypes.object,
    updateViewResult: PropTypes.object,
    deleteViewResult: PropTypes.object,
    runViewResult: PropTypes.object,
    viewData: PropTypes.object,
    viewDataLoading: PropTypes.bool,
    getViewsList: PropTypes.func,
    createView: PropTypes.func,
    updateView: PropTypes.func,
    deleteView: PropTypes.func,
    runView: PropTypes.func,
    getViewData: PropTypes.func,
    runPipeline: PropTypes.func,
    runPipelineResult: PropTypes.object,
};

const mapStateToProps = (state, ownProps) => {
    return {
        getUserDataSetPermissionResult: state.dataSets.getUserDataSetPermissionResult,
        dataSetPermission: state.dataSets.permission,
        getSchemaDataSetResult: state.dataSets.getSchemaDataSetResult,
        recentDataSetListResult: state.dataSets.recentDataSetListResult,
        schemaDataSetResult: state.dataSets.schemaDataSetResult,
        createDataSetResult: state.dataSets.createDataSetResult,
        schemaModel: state.schemaModels.schemaModel,
        schemaPermission: state.schemaModels.permission,
        getDataSetByIdResult: state.dataSets.getDataSetByIdResult,
        dataSet: state.dataSets.dataSet,
        updateDataSetResult: state.dataSets.updateDataSetResult,
        deleteDataSetResult: state.dataSets.deleteDataSetResult,
        getDataObjectsByDataSetIdResult: state.dataObjects.getDataObjectsByDataSetIdResult,
        dataObjectsByDataSetIdResult: state.dataObjects.dataObjectsByDataSetIdResult,
        getDataObjectsByDataSetIdNextPageResult: state.dataObjects.getDataObjectsByDataSetIdNextPageResult,
        getJobsResult: state.dataSets.getJobsResult,
        jobsResult: state.dataSets.jobsResult,
        undoJobResult: state.dataSets.undoJobResult,
        createDataObjectResult: state.dataObjects.createDataObjectResult,
        updateDataObjectResult: state.dataObjects.updateDataObjectResult,
        deleteDataObjectResult: state.dataObjects.deleteDataObjectResult,
        getDataObjectResult: state.dataObjects.getDataObjectResult,
        dataObject: state.dataObjects.dataObject,
        indexDataSetResult: state.dataSets.indexDataSetResult,
        exportDataSetToFileResult: state.dataSets.exportDataSetToFileResult,
        exportDataSetToSystemConnectionResult: state.dataSets.exportDataSetToSystemConnectionResult,
        exportPermission: state.dataSets.exportPermission,
        getDeDuplicatedRecordsRequestResult: state.dataObjectDuplicateSearch.getDeDuplicatedRecordsRequestResult,
        deDuplicatedRecordsResult: state.dataObjectDuplicateSearch.deDuplicatedRecordsResult,
        getDataObjectPermissionResult: state.dataObjects.getDataObjectPermissionResult,
        dataObjectPermission: state.dataObjects.dataObjectPermission,
        getCommentsPermissionResult: state.comments.getCommentsPermissionResult,
        userCommentsPermission: state.comments.userCommentsPermission,
        searchDataObjectsResult: state.dataObjects.searchDataObjectsResult,
        searchDataObjectsLoading: state.dataObjects.searchDataObjectsLoading,
        searchDataObjectsItems: state.dataObjects.searchDataObjectsItems,
        pipelinesResult: state.pipelines.pipelines,
        createPipelineResult: state.pipelines.createPipelineResult,
        updatePipelineResult: state.pipelines.updatePipelineResult,
        deletePipelineResult: state.pipelines.deletePipelineResult,
        dryRunResult: state.pipelines.dryRunResult,
        dryRunLoading: state.pipelines.dryRunLoading,
        runPipelineResult: state.pipelines.runPipelineResult,
        viewsListResult: state.views.viewsListResult,
        createViewResult: state.views.createViewResult,
        updateViewResult: state.views.updateViewResult,
        deleteViewResult: state.views.deleteViewResult,
        runViewResult: state.views.runViewResult,
        viewData: state.views.viewData,
        viewDataLoading: state.views.getViewDataResult?.loading,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        getUserDataSetPermission: () => dispatch(actions.getUserDataSetPermissionRequest()),
        getDataSetBySchemaId: (businessAreaId, schemaId) => dispatch(actions.getDataSetBySchemaIdRequest(businessAreaId, schemaId)),
        createDataSet: (businessAreaId, schemaId, dataSet) => dispatch(actions.createDataSetRequest(businessAreaId, schemaId, dataSet)),
        getDataSetById: (businessAreaId, schemaId, dataSetId) => dispatch(actions.getDataSetByIdRequest(businessAreaId, schemaId, dataSetId)),
        updateDataSet: (businessAreaId, schemaId, dataSet, updatedDataSet) => dispatch(actions.updateDataSetRequest(businessAreaId, schemaId, dataSet, updatedDataSet)),
        deleteDataSet: (businessAreaId, schemaId, dataSetId) => dispatch(actions.deleteDataSetRequest(businessAreaId, schemaId, dataSetId)),
        getDataObjectsByDataSetId: (businessAreaId, schemaId, dataSetId) => dispatch(actions.getDataObjectsByDataSetIdRequest(businessAreaId, schemaId, dataSetId)),
        getDataObjectsByDataSetIdNextPage: (businessAreaId, schemaId, dataSetId, pageKey) =>
            dispatch(actions.getDataObjectsByDataSetIdNextPageRequest(businessAreaId, schemaId, dataSetId, pageKey)),
        getDataObject: (businessAreaId, schemaId, dataSetId, dataObjectId) => dispatch(actions.getDataObjectRequest(businessAreaId, schemaId, dataSetId, dataObjectId)),
        getDataSetJobs: (dataSetId) => dispatch(actions.getDataSetJobsRequest(dataSetId)),
        undoJob: (jobId, version) => dispatch(actions.undoJobRequest(jobId, version)),
        createDataObject: (businessAreaId, schemaId, dataSetId, dataObject) => dispatch(actions.createDataObjectRequest(businessAreaId, schemaId, dataSetId, dataObject)),
        updateDataObject: (businessAreaId, schemaId, dataSetId, dataObjectId, dataObject) =>
            dispatch(actions.updateDataObjectRequest(businessAreaId, schemaId, dataSetId, dataObjectId, dataObject)),
        updateDataObjectValidationErrors: (dataObject, validationErrors) => dispatch(actions.updateDataObjectValidationErrorsRequest(dataObject, validationErrors)),
        deleteDataObject: (businessAreaId, schemaId, dataSetId, dataObjectId) => dispatch(actions.deleteDataObjectRequest(businessAreaId, schemaId, dataSetId, dataObjectId)),
        indexDataSet: (dataSet) => dispatch(actions.indexDataSetRequest(dataSet)),
        exportDataSetToFile: (dataSet) => dispatch(actions.exportDataSetToFileRequest(dataSet)),
        exportDataSetToSystemConnection: (dataSet, connection) => dispatch(actions.exportDataSetToSystemConnectionRequest(dataSet, connection)),
        getUserExportJobPermission: () => dispatch(actions.getUserExportJobPermissionRequest()),
        getDeDuplicatedRecords: (survivedRecordId) => dispatch(actions.getDeDuplicatedRecordsRequest(survivedRecordId)),
        getDataObjectPermission: () => dispatch(actions.getDataObjectPermissionRequest()),
        getCommentsPermission: () => dispatch(actions.getCommentsPermissionRequest()),
        searchDataSetDataObjects: (businessAreaId, schemaId, dataSetId, query) => dispatch(actions.searchDataSetDataObjectsRequest(businessAreaId, schemaId, dataSetId, query)),
        clearSearchDataSetDataObjects: () => dispatch(actions.clearSearchDataSetDataObjects()),
        getPipelines: (datasetId) => dispatch(actions.getPipelinesRequest(datasetId)),
        createPipeline: (pipelineData) => dispatch(actions.createPipelineRequest(pipelineData)),
        updatePipeline: (pipelineId, pipelineData) => dispatch(actions.updatePipelineRequest(pipelineId, pipelineData)),
        deletePipeline: (pipelineId) => dispatch(actions.deletePipelineRequest(pipelineId)),
        dryRunPipelineInline: (datasetId, steps) => dispatch(actions.dryRunPipelineInlineRequest(datasetId, steps)),
        runPipeline: (pipelineId) => dispatch(actions.runPipelineRequest(pipelineId)),
        getViewsList: (filters) => dispatch(actions.getViewsListRequest(filters)),
        createView: (viewData) => dispatch(actions.createViewRequest(viewData)),
        updateView: (viewId, updateData) => dispatch(actions.updateViewRequest(viewId, updateData)),
        deleteView: (viewId) => dispatch(actions.deleteViewRequest(viewId)),
        runView: (viewId) => dispatch(actions.runViewRequest(viewId)),
        getViewData: (viewId) => dispatch(actions.getViewDataRequest(viewId)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(DataSets));
